/**
 * QueueStatsCell — a TESTING element's performance at a glance: just the
 * headline (colored Profit + ROI); the full metric bag lives in a hover
 * tooltip. Queued elements never render this (they have no stats).
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { Tooltip } from '@/components/ui';
import { cn, formatCurrency, formatNumber, formatPercentage } from '@/lib/utils';

interface StatField {
  key: string;
  label: string;
  fmt: (n: number) => string;
}

const FIELDS: StatField[] = [
  { key: 'visits', label: 'Visits', fmt: formatNumber },
  { key: 'clicks', label: 'Clicks', fmt: formatNumber },
  { key: 'conversions', label: 'Conv', fmt: formatNumber },
  { key: 'revenue', label: 'Rev', fmt: (n) => formatCurrency(n) },
  { key: 'cost', label: 'Cost', fmt: (n) => formatCurrency(n) },
  { key: 'profit', label: 'Profit', fmt: (n) => formatCurrency(n) },
  { key: 'roi', label: 'ROI', fmt: (n) => formatPercentage(n) },
];

export const FullStats: FC<{ metrics: Record<string, number> }> = ({ metrics }) => (
  <div className="space-y-0.5 tabular-nums">
    {FIELDS.filter((f) => metrics[f.key] != null).map((f) => (
      <p key={f.key} className="flex justify-between gap-4 text-[11px]">
        <span className="text-muted-foreground">{f.label}</span>
        <span className="font-medium">{f.fmt(metrics[f.key])}</span>
      </p>
    ))}
  </div>
);

/**
 * One metric on its own, for the grouped Testing grid where Profit and ROI are
 * separate aligned columns. An absent value renders "—" and says why on hover —
 * never a fabricated 0, which would read as "measured, and it was zero".
 */
export const MetricValue: FC<{
  metrics?: Record<string, number>;
  field: 'profit' | 'roi';
}> = ({ metrics, field }) => {
  const value = metrics?.[field];
  if (value == null) {
    return (
      <Tooltip content="No stats in the rule's window yet">
        <span tabIndex={0} className="cursor-help text-muted-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring">
          —
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip content={<FullStats metrics={metrics!} />}>
      <span
        tabIndex={0}
        aria-label="Full performance metrics"
        className={cn(
          'cursor-help rounded tabular-nums outline-none focus-visible:ring-1 focus-visible:ring-ring',
          field === 'profit' && 'font-semibold',
          field === 'profit' && value > 0 && 'text-success',
          field === 'profit' && value < 0 && 'text-destructive',
          field === 'roi' && 'text-muted-foreground',
        )}
      >
        {field === 'profit' ? formatCurrency(value) : formatPercentage(value)}
      </span>
    </Tooltip>
  );
};

const QueueStatsCell: FC<{ metrics?: Record<string, number> }> = ({ metrics }) => {
  const has = (k: string) => metrics?.[k] != null;
  if (!metrics || !FIELDS.some((f) => has(f.key))) {
    return <span className="text-muted-foreground">—</span>;
  }
  const profit = metrics.profit;
  const roi = metrics.roi;
  return (
    <Tooltip content={<FullStats metrics={metrics} />}>
      <span
        tabIndex={0}
        aria-label="Full performance metrics"
        className="inline-flex cursor-help items-center gap-2 rounded tabular-nums outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        {profit != null ? (
          <span
            className={cn(
              'text-[11.5px] font-semibold',
              profit > 0 && 'text-success',
              profit < 0 && 'text-destructive',
            )}
          >
            {formatCurrency(profit)}
          </span>
        ) : null}
        {roi != null ? (
          <span className="text-[10.5px] text-muted-foreground">
            ROI {formatPercentage(roi)}
          </span>
        ) : null}
        {profit == null && roi == null ? (
          <span className="text-[10.5px] text-muted-foreground">stats…</span>
        ) : null}
      </span>
    </Tooltip>
  );
};

export default QueueStatsCell;
